import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Button,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { getDoc, doc, setDoc, deleteDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { useLocation } from "react-router-dom";

const Fullview = () => {
  const [data, setData] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uid = params.get("uid");
    if (uid) {
      fetchData(uid);
    }
  }, [location.search]);

  const fetchData = async (uid) => {
    try {
      const docRef = doc(db, "topic", uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setData(docSnap.data());
      } else {
        console.log("No such document!");
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const uid = data.id; // Assuming data has id field as uid
      await setDoc(doc(db, "topic", uid), data);
      setAlertSeverity("success");
      setAlertMessage("Data saved successfully!");
    } catch (err) {
      console.error("Error saving data:", err);
      setAlertSeverity("error");
      setAlertMessage(`Error: ${err.message}`);
    } finally {
      setShowAlert(true);
    }
  };

  const handleDeleteOpen = () => {
    setOpenDeleteDialog(true);
  };

  const handleDeleteClose = () => {
    setOpenDeleteDialog(false);
    setConfirmationText(""); // Reset confirmation text
  };

  const handleDelete = async () => {
    if (confirmationText.toLowerCase() === "delete") {
      try {
        await deleteDoc(doc(db, "topic", data.id)); // Assuming data.id is the uid
        setAlertSeverity("success");
        setAlertMessage("Topic deleted successfully!");
        handleDeleteClose(); // Close dialog
      } catch (error) {
        console.error("Error deleting document:", error);
        setAlertSeverity("error");
        setAlertMessage(`Error: ${error.message}`);
      } finally {
        setShowAlert(true);
      }
    } else {
      setAlertSeverity("error");
      setAlertMessage("Please type 'delete' to confirm deletion.");
      setShowAlert(true);
    }
  };

  const handleAlertClose = () => {
    setShowAlert(false);
  };

  return (
    <div className="App">
      <main style={{ padding: isMobile ? "10px 5px" : "20px 5px" }}>
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={10} md={8} lg={6}>
            <Card style={{ margin: "0 auto" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  color="#272727"
                  align="center"
                  gutterBottom
                >
                  Topic Details View
                </Typography>
                <form onSubmit={handleSave}>
                  <Grid container spacing={isMobile ? 2 : 4}>
                    <Grid item xs={12}>
                      <TextField
                        label="ID"
                        name="id"
                        value={data.id || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        disabled
                      />
                      <TextField
                        label="Title"
                        name="title"
                        value={data.title || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="Router to"
                        name="router_to"
                        value={data.router_to || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="Description"
                        name="description"
                        value={data.description || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={isMobile ? 2 : 4}
                        style={{ marginTop: "16px" }}
                      />
                    </Grid>
                    <Grid container justifyContent="center">
                      <Grid item>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          sx={{
                            borderRadius: 15,
                            boxShadow: "none",
                            width: isMobile ? "100%" : "200px",
                            marginTop: "16px",
                          }}
                        >
                          Update
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          sx={{
                            borderRadius: 15,
                            boxShadow: "none",
                            marginTop: "16px",
                            marginLeft: "16px",
                          }}
                          onClick={handleDeleteOpen}
                        >
                          Delete Topic
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
            <Snackbar
              open={showAlert}
              autoHideDuration={6000}
              onClose={handleAlertClose}
            >
              <Alert
                onClose={handleAlertClose}
                severity={alertSeverity}
                sx={{ width: "100%" }}
              >
                {alertMessage}
              </Alert>
            </Snackbar>
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} onClose={handleDeleteClose}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              Type <strong>"delete"</strong> to confirm deletion.
            </Typography>
            <TextField
              label="Confirmation"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              fullWidth
              variant="outlined"
              margin="dense"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteClose} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDelete} color="secondary">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </main>
    </div>
  );
};

export default Fullview;
